/**
 * ReturnWildlife applier.
 *
 * Returns the drafted wildlife token back to the supply wildlifeBag.
 * Clears draft.tokenInstanceId.
 * Checks for 5+ corridor landmark trigger (same logic as PlaceWildlife —
 * the tile is already placed, so we check corridor from justPlacedTileCoord).
 * Advances phase: placeWildlife → unlockLandmark (if trigger) or endTurn.
 *
 * Per spec §6.4: "ReturnWildlife always legal in placeWildlife phase".
 * Per spec §9.1: the trigger is checked after tile placement completes
 * (either via PlaceWildlife or ReturnWildlife).
 *
 * Spec §6.4, §9.1.
 */

import { LANDMARK_CARDS_BY_ID } from "../data/landmark-cards";
import { TILE_DEFS } from "../data/tile-defs";
import type { ReturnWildlifeAction } from "../types/actions";
import type { AxialCoord } from "../types/coords";
import { coordKey, NEIGHBOUR_OFFSETS } from "../types/coords";
import type { GameState } from "../types/game-state";
import type { Habitat } from "../types/habitat";
import { HABITATS } from "../types/habitat";
import type { Board } from "../types/player";
import { bumpVersion } from "./common-applier";

// ---------------------------------------------------------------------------
// Corridor detection (duplicated from place-wildlife to avoid circular deps)
// ---------------------------------------------------------------------------

const findTileDef = (tileDefId: string) =>
  TILE_DEFS.find((d) => d.tileDefId === tileDefId);

const areHabitatConnectedOnEdge = (
  tileDefIdA: string,
  edgeIdxA: number,
  tileDefIdB: string,
  habitat: Habitat,
): boolean => {
  const defA = findTileDef(tileDefIdA);
  const defB = findTileDef(tileDefIdB);
  if (defA === undefined || defB === undefined) return false;
  const offset = NEIGHBOUR_OFFSETS[edgeIdxA];
  if (offset === undefined) return false;
  return (
    defA.edges[edgeIdxA] === habitat &&
    defB.edges[offset.reciprocal] === habitat
  );
};

const getHabitatCorridor = (
  board: Board,
  start: AxialCoord,
  habitat: Habitat,
): readonly AxialCoord[] => {
  const startCell = board.cells[coordKey(start)];
  if (startCell === undefined) return [];
  const startDef = findTileDef(startCell.tileDefId);
  if (startDef === undefined || !startDef.edges.includes(habitat)) return [];

  const visited = new Set<string>();
  const result: AxialCoord[] = [];
  const queue: AxialCoord[] = [start];
  visited.add(coordKey(start));

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentKey = coordKey(current);
    const currentCell = board.cells[currentKey];
    if (currentCell === undefined) continue;
    result.push(current);

    for (const offset of NEIGHBOUR_OFFSETS) {
      const neighbour: AxialCoord = {
        q: current.q + offset.dq,
        r: current.r + offset.dr,
      };
      const nKey = coordKey(neighbour);
      if (visited.has(nKey)) continue;
      visited.add(nKey);
      const neighbourCell = board.cells[nKey];
      if (neighbourCell === undefined) continue;
      if (
        areHabitatConnectedOnEdge(
          currentCell.tileDefId,
          offset.edge,
          neighbourCell.tileDefId,
          habitat,
        )
      ) {
        queue.push(neighbour);
      }
    }
  }

  return result;
};

const detectLandmarkTriggerHabitats = (
  board: Board,
  placedCoord: AxialCoord,
  existingLandmarkCardIds: readonly string[],
): readonly Habitat[] => {
  const alreadyHasHabitat = new Set<Habitat>();
  for (const cardId of existingLandmarkCardIds) {
    const card = LANDMARK_CARDS_BY_ID[cardId];
    if (card !== undefined) alreadyHasHabitat.add(card.habitat);
  }
  const triggered: Habitat[] = [];
  for (const habitat of HABITATS) {
    if (alreadyHasHabitat.has(habitat)) continue;
    const corridor = getHabitatCorridor(board, placedCoord, habitat);
    if (corridor.length >= 5) triggered.push(habitat);
  }
  return triggered;
};

// ---------------------------------------------------------------------------
// Applier
// ---------------------------------------------------------------------------

export const applyReturnWildlife = (
  state: GameState,
  _action: ReturnWildlifeAction,
): GameState => {
  const activeSeat = state.turn.activeSeat;
  const activePlayer = state.players[activeSeat]!;
  const tokenInstanceId = activePlayer.draft.tokenInstanceId;

  // Return token to bag (push to end — order within bag doesn't matter)
  const newWildlifeBag =
    tokenInstanceId !== null
      ? [...state.supply.wildlifeBag, tokenInstanceId]
      : state.supply.wildlifeBag;

  // Check corridor trigger (same tile board — no wildlife placed)
  const placedCoord = state.turn.actionBudget.justPlacedTileCoord;

  let landmarkEligibleHabitats: readonly Habitat[] = [];
  if (state.ruleset.modules.landmarks && placedCoord !== null) {
    landmarkEligibleHabitats = detectLandmarkTriggerHabitats(
      activePlayer.board,
      placedCoord,
      activePlayer.landmarkPicks,
    );
  }

  const nextPhase =
    landmarkEligibleHabitats.length > 0 ? "unlockLandmark" : "endTurn";

  return {
    ...state,
    ...bumpVersion(state),
    supply: {
      ...state.supply,
      wildlifeBag: newWildlifeBag,
    },
    turn: {
      ...state.turn,
      phase: nextPhase,
      actionBudget: {
        ...state.turn.actionBudget,
        landmarkEligibleHabitats,
      },
    },
    players: state.players.map((p) =>
      p.seat === activeSeat
        ? {
            ...p,
            draft: {
              ...p.draft,
              tokenInstanceId: null,
            },
          }
        : p,
    ),
  };
};
